import { FormItem, EditableSortableList as UiEditableSortableList } from '@faceup/ui'
import type { ComponentProps } from 'react'
import {
  type FieldValues,
  type Path,
  type UseFieldArrayProps,
  useFieldArray,
} from 'react-hook-form'
import { splitPropsBetweenFormInputAndFormItemWrapper } from '../functions'
import type { FormItemWrapperProps } from '../typings'
import { TextInput } from './TextInput'

type Foo = {
  id: string
  label: string
}

type EditableSortableListProps<T extends FieldValues> = UseFieldArrayProps<T> &
  Omit<
    ComponentProps<typeof UiEditableSortableList> & FormItemWrapperProps,
    'items' | 'onMove' | 'onAddOption' | 'children'
  > & {
    shouldDisplayDeleteButton: boolean
  }

export const EditableSortableList = <T extends FieldValues>(
  props: EditableSortableListProps<T>
) => {
  const {
    formItemWrapperProps,
    formInputProps: { shouldDisplayDeleteButton, ...inputProps },
  } = splitPropsBetweenFormInputAndFormItemWrapper(props)
  const { fields, append, remove, move } = useFieldArray({
    control: props.control,
    name: props.name,
  })
  const items = fields as unknown as Foo[]
  return (
    <FormItem {...formItemWrapperProps} key={items.length}>
      <UiEditableSortableList
        {...inputProps}
        items={items}
        onMove={(fromIndex, toIndex) => {
          move(fromIndex, toIndex)
        }}
        onAddOption={() => {
          append({
            // @ts-expect-error no time to fix it
            id: `new-${Date.now()}`,
            label: '',
          })
        }}
      >
        {({ item, index, config }) => (
          <UiEditableSortableList.Item
            key={item.id}
            shouldDisplayDeleteButton={shouldDisplayDeleteButton}
            deleteButtonProps={{
              disabled: !config.isDeletable || inputProps.disabled,
              onClick: () => {
                remove(index)
              },
            }}
          >
            <TextInput
              control={props.control}
              name={`${props.name}.${index}.label` as Path<T>}
              withAsterisk={false}
              disabled={inputProps.disabled}
            />
          </UiEditableSortableList.Item>
        )}
      </UiEditableSortableList>
    </FormItem>
  )
}

EditableSortableList.Item = UiEditableSortableList.Item
